import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { useDeforestationAlerts } from '../../../hooks/useDeforestationAlerts';
import { SupplyShedDriveTime } from '../../../lib/types';
import {
  CHART_AXIS_STROKE,
  CHART_AXIS_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../../lib/chartStyles';

interface LucaMiniChartProps {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
}

const BAR_COLOR = '#F97316'; // matches DeforestationAlertsContent so a viewer who explores LUCA later recognises the palette

const fmtMonthYear = (iso: string): string => {
  const [y, m] = iso.split('-');
  const idx = Number(m) - 1;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (idx < 0 || idx > 11) return iso;
  return `${months[idx]} ${y}`;
};

/**
 * Compact LUCA monthly-loss bar chart for inline use beneath a scenario
 * step's narrative. Exists alongside DeforestationAlertsContent (the full
 * left-panel layer surface) — this is the slimline version: no header, no
 * rolling-mean line, no stat cards. Just the bars + a tiny y-axis tick
 * line so the audience can read scale at a glance.
 */
export const LucaMiniChart: React.FC<LucaMiniChartProps> = ({
  millCode,
  driveTime,
}) => {
  const { data, isLoading, error } = useDeforestationAlerts(millCode, driveTime);

  if (error) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-3 border border-red-500/40 text-body-sm font-roboto text-red-400">
        LUCA disturbance feed unavailable.
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-3 border border-figma-lime/30 text-body-sm font-roboto text-neutral-400">
        Loading recent disturbance pulse…
      </div>
    );
  }

  if (data.points.length === 0) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-3 border border-figma-lime/30 text-body-sm font-roboto text-neutral-400">
        No LUCA disturbance alerts recorded for this shed.
      </div>
    );
  }

  const firstDate = data.points[0].date;
  const lastDate  = data.points[data.points.length - 1].date;
  const yearTicks = data.points.filter((p) => p.month === 1).map((p) => p.date);

  return (
    <div className="bg-figma-card rounded-figma-panel p-3 border border-figma-lime/30 shadow-figma-card">
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-[11px] font-roboto-mono text-neutral-400 uppercase tracking-wider">
          LUCA disturbance · monthly hectares
        </p>
        <p className="text-[10px] font-roboto-mono text-neutral-500">
          {fmtMonthYear(firstDate)} – {fmtMonthYear(lastDate)}
        </p>
      </div>
      <ResponsiveContainer width="100%" height={120} minWidth={0}>
        <BarChart data={data.points} margin={{ top: 2, right: 8, left: 0, bottom: 0 }} barCategoryGap={1}>
          <XAxis
            dataKey="date"
            stroke={CHART_AXIS_STROKE}
            style={CHART_AXIS_STYLE}
            tickMargin={4}
            ticks={yearTicks}
            tickFormatter={(v: string) => v.slice(0, 4)}
          />
          <YAxis
            stroke={CHART_AXIS_STROKE}
            style={CHART_AXIS_STYLE}
            width={36}
            tickFormatter={(v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)
            }
          />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL_STYLE}
            itemStyle={CHART_TOOLTIP_ITEM_STYLE}
            formatter={(v: number | undefined) => [
              v != null ? `${Math.round(v).toLocaleString('en-US')} ha` : '—',
              'Detected loss',
            ]}
            labelFormatter={(label) => fmtMonthYear(String(label))}
          />
          <Bar dataKey="areaHa" fill={BAR_COLOR} fillOpacity={0.85} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <p className="mt-1 text-[10px] font-roboto italic text-neutral-500 leading-relaxed">
        Detected disturbance, not deforestation — LUCA flags loss without
        attributing it to harvest, wildfire, or insect.
      </p>
    </div>
  );
};
